/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsApproveSuggestion } from "../funcs/skillsApproveSuggestion.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ApproveSkillSuggestionResult } from "../models/components/approveskillsuggestionresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ApproveSkillSuggestionRequest,
  ApproveSkillSuggestionSecurity,
} from "../models/operations/approveskillsuggestion.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ApproveSkillSuggestionMutationVariables = {
  request: ApproveSkillSuggestionRequest;
  security?: ApproveSkillSuggestionSecurity | undefined;
  options?: RequestOptions;
};

export type ApproveSkillSuggestionMutationData = ApproveSkillSuggestionResult;

export type ApproveSkillSuggestionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * approveSuggestion skills
 *
 * @remarks
 * Approve an open skill edit suggestion, optionally replacing its proposed SKILL.md content or taking only a subset of its proposed changes. Stale suggestions are superseded instead.
 */
export function useApproveSkillSuggestionMutation(
  options?: MutationHookOptions<
    ApproveSkillSuggestionMutationData,
    ApproveSkillSuggestionMutationError,
    ApproveSkillSuggestionMutationVariables
  >,
): UseMutationResult<
  ApproveSkillSuggestionMutationData,
  ApproveSkillSuggestionMutationError,
  ApproveSkillSuggestionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildApproveSkillSuggestionMutation(client, options),
    ...options,
  });
}

export function mutationKeyApproveSkillSuggestion(): MutationKey {
  return ["@gram/client", "skills", "approveSuggestion"];
}

export function buildApproveSkillSuggestionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ApproveSkillSuggestionMutationVariables,
  ) => Promise<ApproveSkillSuggestionMutationData>;
} {
  return {
    mutationKey: mutationKeyApproveSkillSuggestion(),
    mutationFn: function approveSkillSuggestionMutationFn({
      request,
      security,
      options,
    }): Promise<ApproveSkillSuggestionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsApproveSuggestion(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
